import PropTypes from "prop-types";
import { FormattedMessage, useIntl } from "react-intl";
import { Typography } from "core/typography/Typography";
import NumberStepper from "core/inputs/number-stepper/NumberStepper";
import InputDate from "core/inputs/input-date/InputDate";
import StepHeader from "./StepHeader";
import FafField from "../FafField";

export const emptyProspect = () => ({
  // Stable React key so shrinking the list (counter down) never reassociates another
  // card's in-progress input (see emptyNeed) — and a persisted card keeps a constant key.
  uid: crypto.randomUUID(),
  first_name: "",
  last_name: "",
  company: "",
  prospection_date: "",
  linkedin_url: "",
});

// A prospection card is blank while none of its fields has been filled in.
const isBlank = (p) =>
  !p.first_name &&
  !p.last_name &&
  !p.company &&
  !p.prospection_date &&
  !p.linkedin_url;

// Step 4 — prospecting: counter + dynamic list of prospections.
// Labelled "Prospections" in the UI — deliberately the same word as the client Prospections tab.
// The counter drives the number of prospection blocks, like the Absences/Interviews steps.
const SourcingStep = ({ prospects, setProspects, lang, showErrors }) => {
  const intl = useIntl();

  const setCount = (n) =>
    setProspects((prev) => {
      if (n >= prev.length)
        return [
          ...prev,
          ...Array.from({ length: n - prev.length }, emptyProspect),
        ];
      // The counter only drops trailing BLANK cards: a card with any input — a fortiori a
      // persisted one, which ProspectProvider DELETEs server-side on commit — can leave only
      // via its explicit "Supprimer" button, so decrementing never destroys data silently.
      return prev.slice(n).every(isBlank) ? prev.slice(0, n) : prev;
    });

  const removeProspect = (index) =>
    setProspects((prev) => prev.filter((_, i) => i !== index));

  const setField = (index, field, value) =>
    setProspects((prev) =>
      prev.map((p, i) => (i === index ? { ...p, [field]: value } : p))
    );

  return (
    <div className="faf-step">
      <StepHeader>
        <FormattedMessage
          id="tools_manager_faf_sourcing_title"
          defaultMessage="Tes prospections de la semaine"
        />
      </StepHeader>

      <div className="faf-count-row">
        <span className="faf-field-label">
          <FormattedMessage
            id="tools_manager_faf_sourcing_count_label"
            defaultMessage="Nombre total de prospections"
          />
        </span>
        <NumberStepper
          id="faf-sourcing-count"
          dataTestId="faf-sourcing-count"
          value={prospects.length}
          onChange={setCount}
          min={0}
        />
      </div>

      {prospects.length === 0 && (
        <Typography
          type="p"
          className="faf-help text-3"
          label={
            <FormattedMessage
              id="tools_manager_faf_sourcing_empty_hint"
              defaultMessage="Augmente le compteur pour ajouter une prospection."
            />
          }
        />
      )}

      {prospects.map((p, i) => (
        <div className="faf-rowcard" key={p.uid ?? p.id ?? i}>
          <div className="faf-rowcard-head">
            <div className="faf-rowcard-idx">
              <FormattedMessage
                id="tools_manager_faf_sourcing_candidate_n"
                defaultMessage="Prospection #{n}"
                values={{ n: i + 1 }}
              />
            </div>
            <button
              type="button"
              className="faf-need-remove"
              data-testid={`faf-sourcing-${i}-remove`}
              onClick={() => removeProspect(i)}
            >
              <FormattedMessage
                id="tools_manager_faf_remove"
                defaultMessage="Supprimer"
              />
            </button>
          </div>
          <div className="faf-fields-3">
            <FafField
              label={{
                id: "tools_manager_faf_field_first_name",
                defaultMessage: "Prénom",
              }}
              required
              invalid={showErrors?.[i] && !(p.first_name || "").trim()}
            >
              <input
                className="faf-input"
                data-testid={`faf-sourcing-${i}-first-name`}
                value={p.first_name}
                onChange={(e) => setField(i, "first_name", e.target.value)}
              />
            </FafField>
            <FafField
              label={{
                id: "tools_manager_faf_field_last_name",
                defaultMessage: "Nom",
              }}
              required
              invalid={showErrors?.[i] && !(p.last_name || "").trim()}
            >
              <input
                className="faf-input"
                data-testid={`faf-sourcing-${i}-last-name`}
                value={p.last_name}
                onChange={(e) => setField(i, "last_name", e.target.value)}
              />
            </FafField>
            <FafField
              label={{
                id: "tools_manager_faf_field_company",
                defaultMessage: "Société",
              }}
              required
              invalid={showErrors?.[i] && !(p.company || "").trim()}
            >
              <input
                className="faf-input"
                data-testid={`faf-sourcing-${i}-company`}
                value={p.company}
                onChange={(e) => setField(i, "company", e.target.value)}
              />
            </FafField>
          </div>
          <div className="faf-fields-2">
            <FafField
              label={{
                id: "tools_manager_faf_field_date",
                defaultMessage: "Date",
              }}
              required
              invalid={showErrors?.[i] && !p.prospection_date}
            >
              <InputDate
                id={`faf-prospect-date-${i}`}
                name={`faf-sourcing-${i}-date`}
                value={p.prospection_date}
                lang={lang}
                maxDate={new Date()}
                onChange={(e) => setField(i, "prospection_date", e.target.value)}
              />
            </FafField>
            <FafField
              label={{
                id: "tools_manager_faf_field_linkedin",
                defaultMessage: "LinkedIn (facultatif)",
              }}
            >
              <input
                className="faf-input"
                data-testid={`faf-sourcing-${i}-linkedin`}
                placeholder={intl.formatMessage({
                  id: "tools_manager_faf_field_linkedin_ph",
                  defaultMessage: "https://linkedin.com/in/…",
                })}
                value={p.linkedin_url}
                onChange={(e) => setField(i, "linkedin_url", e.target.value)}
              />
            </FafField>
          </div>
        </div>
      ))}
    </div>
  );
};

SourcingStep.propTypes = {
  prospects: PropTypes.array.isRequired,
  setProspects: PropTypes.func.isRequired,
  lang: PropTypes.string,
  showErrors: PropTypes.array,
};

export default SourcingStep;
